import { ComponentProps } from 'react';
import clsx from 'clsx';
import { toast, ToastOptions } from 'react-toastify';

import ToastError from '~/shared/assets/icons/circled-cancel-filled.svg';
import ToastInfo from '~/shared/assets/icons/info.svg';
import ToastSuccess from '~/shared/assets/icons/circled-check-filled.svg';
import ToastWarning from '~/shared/assets/icons/exclamation-fill.svg';

import Typography from '../components/Typography';

type Variant = 'info' | 'warning' | 'error' | 'success';
type VariantStyleItem = {
  Icon: typeof ToastInfo;
  bg: string;
  text: string;
  border: string;
  fill: string;
};
// eslint-disable-next-line react-refresh/only-export-components
function Toast({ title, message, variant }: { title: string; message: string; variant: Variant }) {
  const { bg, Icon, text, border, fill } = variantsStylesMap[variant];

  return (
    <div
      className={clsx(
        'flex gap-[30px] items-center w-fit p-[12px] border-l-[6px] rounded-tl-[12px] rounded-bl-[12px]',
        border
      )}
    >
      <div
        className={clsx(
          'w-[75px] aspect-square rounded-[18.75px] flex items-center justify-center',
          bg
        )}
      >
        <Icon className={clsx('w-[50px] aspect-square', fill)} />
      </div>

      <div className="flex flex-col py-[7px] gap-[8px]">
        <Typography variant="H3" color={text}>
          {title}
        </Typography>
        <div className="max-w-[682px] overflow-x-auto whitespace-nowrap">
          <Typography variant="H4" color="text-grey">
            {message}
          </Typography>
        </div>
      </div>
    </div>
  );
}

type ToastProps = ComponentProps<typeof Toast>;

export const toaster = ({ title, message, variant, ...rest }: ToastProps & ToastOptions) =>
  toast(<Toast {...{ title, message, variant }} />, { ...rest });

const variantsStylesMap: {
  [k in Variant]: VariantStyleItem;
} = {
  info: {
    Icon: ToastInfo,
    bg: 'bg-primary-100',
    text: 'text-primary-400',
    border: 'border-primary-100',
    fill: 'fill-primary-400',
  },
  error: {
    Icon: ToastError,
    bg: 'bg-light-red',
    text: 'text-red',
    border: 'border-light-red',
    fill: 'fill-red',
  },
  warning: {
    Icon: ToastWarning,
    bg: 'bg-light-orange',
    text: 'text-orange',
    border: 'border-light-orange',
    fill: 'fill-orange',
  },
  success: {
    Icon: ToastSuccess,
    bg: 'bg-light-green',
    text: 'text-green',
    border: 'border-light-green',
    fill: 'fill-green',
  },
};
